import React from 'react';
import PropTypes from 'prop-types';

import CircularProgress from '@material-ui/core/CircularProgress';

import some from 'lodash/some';
import { useHistory } from 'react-router-dom';
import intersection from 'lodash/intersection';
import { GroupClientsFiltersPropTypes } from 'utils/PropTypesValues';
import { clientAssetsQueryParams } from 'sharedComponents/FilterChips/filterUtils';

import CreateProfileButton from '../ClientSearchModal/components/CreateProfileButton';
import AddProfileForm from '../ClientSearchModal/components/AddProfileForm';
import Drawer from '../Drawer';
import ClientListItemGeneral from '../ClientListItemGeneral';
import ClientListItemEmpty from '../ClientListItemEmpty';
import AlphabetNavigation from '../AlphabetNavigation';

const AutoSizer = React.lazy(() => import('react-virtualized/dist/es/AutoSizer'));
const InfiniteLoader = React.lazy(() => import('react-virtualized/dist/es/InfiniteLoader'));
const List = React.lazy(() => import('react-virtualized/dist/es/List'));

const STATUS_LOADING = 1;
const STATUS_LOADED = 2;

const FIRST_FETCH_SIZE = 30;

const filtersAreEqual = (currentFilters, filters, sort) => {
  if (currentFilters?.sort !== sort) {
    return false;
  }

  return !some(filters, (filterValue, filterName) => currentFilters[filterName] !== filterValue);
};

const ClientsInfiniteScrollList = ({
  classes,
  clients,
  selectClient,
  contactType,
  itemRenderer,
  customClientClickAction,
  allowClickOnIcon,
  selectedClients,
  selectionEnabled,
  loading,
  clientsStats,
  customerFilterParams,
  saveInfiniteScrollPosition,
  initialFirstVisibleItemIndex,
  lastExternalModifications,
  clearClientsCache,
  useAsSearch,
  recentSearchList,
  setRecentSearch,
  withTopExtraSpace,
  fetch,
  filters,
  displayAlphabet,
  search,
  searchField,
  searchLevel,
  sort,
  showAddProfileButton,
  isInboxLoading,
  groupId,
}) => {
  const isEmptySearchState = () => {
    return useAsSearch && search === '';
  };

  const getClientsCount = () => {
    if (isEmptySearchState()) {
      return recentSearchList.length;
    }

    return clientsStats.totalCount;
  };

  const getClientsSource = () => {
    if (isEmptySearchState()) {
      return recentSearchList.reduce((acc, val, i) => {
        acc[i] = val;
        return acc;
      }, {});
    }

    return clients;
  };

  const clientsSource = getClientsSource();
  const clientsCount = getClientsCount();

  const getInitialLoadedState = () => {
    const loadedState = {};
    if (isEmptySearchState()) {
      recentSearchList.forEach((val, index) => {
        loadedState[index] = STATUS_LOADED;
      });
    } else {
      Object.keys(clients).forEach((index) => {
        if (clients[index]) {
          loadedState[index] = STATUS_LOADED;
        }
      });
    }

    return loadedState;
  };

  // The height of the item can be dynamic only if that is dependency on it's index
  // According to https://jira.mynmg.com/browse/OSP-6165 we should show additional rows for each
  // list item if specific filters are selected.
  const listItemHeight = React.useMemo(() => {
    const defaultHeight = 72;
    if (filters.withAssets) {
      const selectedAssetsOptions = filters.withAssets.split(',');
      const possibleAssetsOptions = [clientAssetsQueryParams.promo, clientAssetsQueryParams.point];
      const additionalRowsAmount = intersection(selectedAssetsOptions, possibleAssetsOptions).length;
      const additionalRowHeight = 20;

      return defaultHeight + additionalRowHeight * additionalRowsAmount;
    }
    return defaultHeight;
  }, [filters]);

  const [listRef, setListRef] = React.useState();
  const [firstLoad, setFirstLoad] = React.useState(true);
  const [loadedRowsMap, setLoadedRowsMap] = React.useState(getInitialLoadedState());
  const [firstVisibleLetter, setFirstVisibleLetter] = React.useState('A');
  const [firstVisibleIndex, setFirstVisibleIndex] = React.useState(0);

  const hasLoadedClients = () => {
    return !!Object.keys(clientsSource).length;
  };

  const displayEmptySearchText = () => {
    if (useAsSearch && search.length === 1) {
      return true;
    }

    return !hasLoadedClients() && !firstLoad;
  };

  React.useEffect(() => {
    const fromIndex = 0;
    const size = FIRST_FETCH_SIZE;
    if (useAsSearch) {
      if (search.length >= 2) {
        fetch(fromIndex, size, { q: search, field: searchField, searchLevel }).then(() => {
          setFirstLoad(false);
        });
      }

      if (search.length === 0) {
        setLoadedRowsMap(getInitialLoadedState());
      } else {
        clearClientsCache();
        setLoadedRowsMap({});
      }
      return () => {
        clearClientsCache();
        setLoadedRowsMap({});
      };
    }

    const equal = filtersAreEqual(customerFilterParams, filters, sort);
    if (!equal) {
      clearClientsCache();
      setLoadedRowsMap({});
    }

    fetch(fromIndex, size, { sort, filters }).then(() => {
      setFirstLoad(false);
    });
  }, [sort, filters, search, searchField, searchLevel, groupId]);

  // refresh clients list in case of `lastExternalModifications` prop has been updated
  React.useEffect(() => {
    if (!lastExternalModifications) {
      // no need to execute this use effect for initial rendering
      return;
    }

    clearClientsCache();

    let fetchPromise;

    if (useAsSearch) {
      fetchPromise = fetch(0, FIRST_FETCH_SIZE, { q: search, field: searchField, searchLevel });
    } else {
      fetchPromise = fetch(0, FIRST_FETCH_SIZE, { sort, filters });
    }

    setLoadedRowsMap({});
    fetchPromise.then(() => {
      setFirstLoad(false);
    });
  }, [lastExternalModifications]);

  const scroll = (letter) => {
    const row = clientsStats.alphabeticalMap[letter];
    setFirstVisibleLetter(letter.toUpperCase());
    listRef.scrollToRow(row);
  };

  React.useEffect(() => {
    if (listRef && initialFirstVisibleItemIndex) {
      listRef.scrollToRow(initialFirstVisibleItemIndex);
    }
  }, [listRef]);

  const shouldRenderAlphabet = () =>
    displayAlphabet && clientsCount > 20 && !!Object.keys(clientsStats.alphabeticalMap).length;

  // check for search.length because of showing recentSearchItems when search length === 0;
  // if we do it in hook, than it runs after re-render and can't perform in-time update of loadedRowsMap
  const isRowLoaded = ({ index }) => {
    return (useAsSearch && search.length === 0) || !!loadedRowsMap[index];
  }; // STATUS_LOADING or STATUS_LOADED

  const loadMoreRows = ({ startIndex, stopIndex }) => {
    let allRowsAreAlreadyLoaded = true;
    for (let i = startIndex; i <= stopIndex; i += 1) {
      if (clients[i]) {
        loadedRowsMap[i] = STATUS_LOADED;
      } else {
        loadedRowsMap[i] = STATUS_LOADING;
        allRowsAreAlreadyLoaded = false;
      }
    }

    if (allRowsAreAlreadyLoaded) {
      return Promise.resolve();
    }

    let fetchPromise;

    const from = startIndex;
    const size = stopIndex - startIndex + 1;

    if (search !== undefined) {
      fetchPromise = fetch(from, size, { q: search, field: searchField, searchLevel });
    } else {
      fetchPromise = fetch(from, size, { sort, filters });
    }

    return fetchPromise.then((data) => {
      for (let i = startIndex; i <= stopIndex; i += 1) {
        loadedRowsMap[i] = STATUS_LOADED;
      }
      return data;
    });
  };

  const rowsRendered = ({ startIndex }) => {
    if (clients && clients[startIndex]) {
      const startLetter = clients[startIndex].firstName.charAt(0);
      setFirstVisibleLetter(startLetter.toUpperCase());
      setFirstVisibleIndex(startIndex);
    }
  };

  const beforeGoToProfilePage = (client) => {
    if (useAsSearch) {
      setRecentSearch(`${client.firstName} ${client.lastName}`, client);
      return;
    }

    if (saveInfiniteScrollPosition) {
      saveInfiniteScrollPosition(firstVisibleIndex);
    }
  };

  const getRenderItem = () => {
    return itemRenderer || ClientListItemGeneral;
  };

  const rowRenderer = ({ index, key, style }) => {
    const client = clientsSource[index];

    const RowComponent = getRenderItem();
    const content = client ? (
      <RowComponent
        customClientClickAction={customClientClickAction}
        allowClickOnIcon={allowClickOnIcon}
        selectedClients={selectedClients}
        selectClient={selectClient}
        withAlphabet={shouldRenderAlphabet()}
        listIndex={index}
        contactType={contactType}
        selectionEnabled={selectionEnabled}
        beforeGoToProfilePage={() => beforeGoToProfilePage(client)}
        key={client.profileId}
        client={client}
        filters={filters}
      />
    ) : (
      <ClientListItemEmpty key={key} />
    );

    return (
      <div key={key} style={style}>
        {content}
      </div>
    );
  };
  const [profileFormVisible, setProfileFormVisible] = React.useState(false);
  const history = useHistory();

  if (showAddProfileButton) {
    return (
      <>
        <Drawer
          title="New Client"
          fullWidth
          open={profileFormVisible}
          onClose={() => {
            setProfileFormVisible(false);
          }}
        >
          <AddProfileForm searchTerm={search} searchBy={searchField} history={history} />
        </Drawer>
        <CreateProfileButton
          onClick={() => {
            setProfileFormVisible(true);
          }}
          className={classes.createProfile}
        />
      </>
    );
  }

  return (
    <>
      {loading && !isInboxLoading ? (
        <CircularProgress className={classes.loader} />
      ) : (
        <>
          {isEmptySearchState() && (
            <div className={classes.title} data-testid="recent-clients-header">
              Recent Searches
            </div>
          )}
          <div
            data-testid="scroll-area-wrapper"
            className={`${withTopExtraSpace ? classes.scrollAreaWrapperWithTopSpace : classes.scrollAreaWrapper}
          ${isEmptySearchState() ? classes.scrollAreaWithRecentSearches : ''}
          `}
          >
            {clientsCount ? (
              <>
                <div
                  className={shouldRenderAlphabet() ? classes.listWrapper : classes.listWrapperNoAlphabet}
                  data-testid="clients-list-wrapper"
                >
                  <InfiniteLoader isRowLoaded={isRowLoaded} loadMoreRows={loadMoreRows} rowCount={clientsCount}>
                    {({ onRowsRendered, registerChild }) => (
                      <AutoSizer>
                        {({ width, height }) => (
                          <List
                            scrollToAlignment="start"
                            ref={(list) => {
                              registerChild(list);
                              setListRef(list);
                            }}
                            className={
                              shouldRenderAlphabet() ? classes.virtualizedList : classes.virtualizedListNoAlphabet
                            }
                            height={height}
                            onRowsRendered={(...arg) => {
                              onRowsRendered(...arg);
                              rowsRendered(...arg);
                            }}
                            rowCount={clientsCount}
                            rowHeight={listItemHeight}
                            rowRenderer={rowRenderer}
                            width={width}
                          />
                        )}
                      </AutoSizer>
                    )}
                  </InfiniteLoader>
                </div>
                {shouldRenderAlphabet() && (
                  <AlphabetNavigation
                    alphabeticalMap={clientsStats.alphabeticalMap}
                    activeLetter={firstVisibleLetter}
                    setActiveLetter={setFirstVisibleLetter}
                    scroll={scroll}
                  />
                )}
              </>
            ) : (
              displayEmptySearchText() && (
                <div className={classes.empty} data-testid="no-records-found">
                  No records found
                </div>
              )
            )}
          </div>
        </>
      )}
    </>
  );
};

ClientsInfiniteScrollList.propTypes = {
  classes: PropTypes.objectOf(PropTypes.string),
  clients: PropTypes.shape({
    firstName: PropTypes.string,
    profileId: PropTypes.string,
  }),
  loading: PropTypes.bool,
  displayAlphabet: PropTypes.bool,
  selectClient: PropTypes.func,
  selectedClients: PropTypes.arrayOf(PropTypes.shape({ profileId: PropTypes.string })),
  clientsStats: PropTypes.shape({
    alphabeticalMap: PropTypes.objectOf(PropTypes.number),
    totalCount: PropTypes.number,
  }),
  customerFilterParams: PropTypes.objectOf(PropTypes.any),
  saveInfiniteScrollPosition: PropTypes.func,
  initialFirstVisibleItemIndex: PropTypes.number,
  clearClientsCache: PropTypes.func,
  fetch: PropTypes.func,
  match: PropTypes.shape({
    url: PropTypes.string,
    params: PropTypes.shape({}),
  }),
  filters: GroupClientsFiltersPropTypes,
  sort: PropTypes.string,
  lastExternalModifications: PropTypes.number,
  recentSearchList: PropTypes.arrayOf(PropTypes.any),
  showAddProfileButton: PropTypes.bool,
  withTopExtraSpace: PropTypes.bool,
  groupId: PropTypes.string,
};

ClientsInfiniteScrollList.defaultProps = {
  classes: {},
  clients: {},
  clientsStats: {},
  lastExternalModifications: null,
  displayAlphabet: true,
  initialFirstVisibleItemIndex: 0,
  filters: {},
  withTopExtraSpace: false,
  sort: '',
  fetch: () => null,
  recentSearchList: [],
  groupId: '',
};

export default ClientsInfiniteScrollList;
